/**
 * What this browser last heard from AFAR Connect about the signed-in account: its Cloud Workspaces
 * and its Usage Allowance. Every surface that shows either reads it from here rather than asking
 * Connect itself, and Connect is asked once when the page is used: when it opens, when the user
 * comes back to it after the snapshot went stale, right after this page changed something, and
 * when a connection reports that a workspace stopped or failed.
 *
 * Nothing here runs on a timer. A page left open makes no Connect requests, because every one of
 * them reaches Connect's database and a steady trickle keeps it from ever pausing.
 */
import type { CloudWorkspaceConnectOutcome, ConnectDesktopBridge } from "@afar-research/contracts";
import { useEffect, useSyncExternalStore } from "react";

import { readLocalApi } from "../localApi";
import { connectAccountKey } from "./connectEnvironments";
import {
  type ConnectSnapshot,
  connectSnapshotNeedsCheck,
  readStoredConnectSnapshot,
  storeConnectSnapshot,
} from "./connectSnapshot.logic";

const STORAGE_KEY = "afar-research:connect-snapshot";
const ACCOUNT_CHANGED = "afar-connect-account-changed";

const loadStored = (): ConnectSnapshot | null => {
  try {
    return readStoredConnectSnapshot(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

let current: ConnectSnapshot | null = typeof window === "undefined" ? null : loadStored();
let checkedThisPage = false;
let checking: Promise<ConnectSnapshot | null> | null = null;
/** Each check's number; only the latest one started may change the snapshot. */
let generation = 0;
const reconciledRefusals = new Map<string, "workspace_stopped" | "workspace_failed">();
const listeners = new Set<() => void>();

const publish = (next: ConnectSnapshot | null) => {
  current = next;
  try {
    if (next === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, storeConnectSnapshot(next));
  } catch {
    // Storage is a convenience: the page still shows what it heard this session.
  }
  for (const listener of listeners) listener();
};

export function getConnectSnapshot(): ConnectSnapshot | null {
  return current;
}

export function subscribeConnectSnapshot(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Null until this browser has heard from Connect for a signed-in account. */
export function useConnectSnapshot(): ConnectSnapshot | null {
  return useSyncExternalStore(subscribeConnectSnapshot, getConnectSnapshot, () => null);
}

type SnapshotBridge = Pick<
  ConnectDesktopBridge,
  "getStatus" | "cloudWorkspaces" | "cloudAllowance"
>;

/** How a surface asks; see `checkConnectSnapshot`. */
export interface ConnectSnapshotCheck {
  readonly force?: boolean;
  readonly bridge?: SnapshotBridge;
  /** How old an answer may be and still do, for a surface watching a change settle. */
  readonly freshMs?: number;
  /** Read only the workspaces, keeping the allowance already heard. */
  readonly workspacesOnly?: boolean;
}

const check = async (
  options: ConnectSnapshotCheck & { readonly force: boolean },
  bridge: SnapshotBridge | undefined,
  isLatest: () => boolean,
): Promise<ConnectSnapshot | null> => {
  if (!bridge || (!bridge.cloudWorkspaces && !bridge.cloudAllowance)) return null;
  // Read from this device; it never waits on the identity service.
  const status = await bridge.getStatus();
  if (!isLatest()) return current;
  if (status.state !== "signed-in") {
    if (current !== null) publish(null);
    return null;
  }
  const accountKey = connectAccountKey(status.activeAccountId);
  // Another account's answer is never shown, not even while this one is read.
  if (current !== null && current.accountKey !== accountKey) publish(null);
  if (
    !connectSnapshotNeedsCheck({
      snapshot: current,
      accountKey,
      now: Date.now(),
      force: options.force,
      ...(options.freshMs === undefined ? {} : { freshMs: options.freshMs }),
    })
  )
    return current;
  const previousAllowance = current?.allowance ?? null;
  const [workspaces, allowance] = await Promise.all([
    bridge.cloudWorkspaces ? bridge.cloudWorkspaces() : [],
    // The allowance only decorates the list; failing to read it keeps what was shown.
    bridge.cloudAllowance && !(options.workspacesOnly && previousAllowance !== null)
      ? bridge.cloudAllowance().catch(() => previousAllowance)
      : previousAllowance,
  ]);
  const next: ConnectSnapshot = { accountKey, workspaces, allowance, checkedAt: Date.now() };
  // A check overtaken by a newer one read before it, so its answer is not the latest.
  if (!isLatest()) return next;
  publish(next);
  return next;
};

/**
 * Asks Connect, unless the snapshot is still fresh. The first check on a page always asks, and
 * `force` asks regardless: the user pressed Refresh, or this page just changed a workspace.
 * Callers that arrive together share one request. A forced one starts at once and overtakes a check
 * in flight, so a stalled read never holds up the user's retry and an answer read before a change
 * never replaces one read after it. A failure leaves the snapshot as it was and rejects.
 */
export function checkConnectSnapshot(
  options: ConnectSnapshotCheck = {},
): Promise<ConnectSnapshot | null> {
  const force = options.force === true || !checkedThisPage;
  if (checking !== null && !force) return checking;
  checkedThisPage = true;
  const mine = ++generation;
  const run = check(
    { ...options, force },
    options.bridge ?? readLocalApi()?.connect,
    () => mine === generation,
  );
  const shared = run.finally(() => {
    if (checking === shared) checking = null;
  });
  checking = shared;
  return shared;
}

/** Reconcile authoritative connection refusals once per account, workspace and refusal episode. */
export function reconcileConnectWorkspaceOutcome(
  workspaceId: string,
  outcome: CloudWorkspaceConnectOutcome,
): void {
  if (current === null || !current.workspaces.some((workspace) => workspace.id === workspaceId))
    return;
  const key = JSON.stringify([current.accountKey, workspaceId]);
  if (
    outcome._tag !== "CloudWorkspaceRefused" ||
    (outcome.code !== "workspace_stopped" && outcome.code !== "workspace_failed")
  ) {
    reconciledRefusals.delete(key);
    return;
  }
  if (reconciledRefusals.get(key) === outcome.code) return;
  reconciledRefusals.set(key, outcome.code);
  // Refresh failure keeps the last answer; repeated connection attempts must not become polling.
  void checkConnectSnapshot({ force: true, workspacesOnly: true }).catch(() => undefined);
}

/**
 * The snapshot, checked when this page is used: on mount, and when the window comes back into focus
 * or view after the snapshot went stale. Failures are left for a surface that reports them.
 */
export function useConnectSnapshotCheckedOnUse(): ConnectSnapshot | null {
  useEffect(() => {
    const use = () => void checkConnectSnapshot().catch(() => undefined);
    const visible = () => {
      if (document.visibilityState === "visible") use();
    };
    use();
    window.addEventListener("focus", use);
    document.addEventListener("visibilitychange", visible);
    const unsubscribeAccount = subscribeConnectAccountChanged(use);
    return () => {
      window.removeEventListener("focus", use);
      document.removeEventListener("visibilitychange", visible);
      unsubscribeAccount();
    };
  }, []);
  return useConnectSnapshot();
}

/**
 * Drops what this browser heard, and any check still in flight: the next check asks Connect. For
 * an account change, and for tests that each start from nothing.
 */
export function forgetConnectSnapshot(): void {
  generation += 1;
  checking = null;
  checkedThisPage = false;
  reconciledRefusals.clear();
  publish(null);
}

/**
 * The signed-in account changed on this page: sign-in, sign-out, or a switch. What was heard for
 * the previous account is dropped at once, and every surface showing it reads the new one.
 */
export function notifyConnectAccountChanged(): void {
  forgetConnectSnapshot();
  window.dispatchEvent(new Event(ACCOUNT_CHANGED));
}

export function subscribeConnectAccountChanged(listener: () => void): () => void {
  window.addEventListener(ACCOUNT_CHANGED, listener);
  return () => window.removeEventListener(ACCOUNT_CHANGED, listener);
}
